/**
 * Контролы рабочих экранов: ввод, выбор, набор значений, переключатель,
 * подтверждение необратимого.
 *
 * Каркас даёт `Field` — подпись, обёртку и строку под полем, которая не
 * прыгает, когда вместо подсказки встаёт ошибка (app-kit.md §2). Здесь на нём
 * собраны сами поля, потому что экраны брендов, ядра и прогонов — это в
 * основном формы, и повторять в каждом одну и ту же обвязку `<Field>` вокруг
 * `<input>` значит семь раз написать `aria-describedby` и один раз забыть.
 *
 * Чего здесь нет намеренно:
 *
 * * **своего модального окна** — подтверждение собрано на `Dialog` каркаса,
 *   у него уже есть возврат фокуса, Escape и запрет прокрутки под окном;
 * * **звёздочки у обязательных полей** — по app-kit.md §2 помечается то, чего
 *   меньше, а в формах кабинета обязательно почти всё, поэтому помечено
 *   необязательное;
 * * **`outline: none`** нигде и ни у чего.
 *
 * design: derived, not documented — app-kit.md §2 и §4.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';

import { Field } from './form';
import { Dialog } from './Dialog';

/* ─────────────────────────────── строка ──────────────────────────────────── */

export function TextField({
  label,
  value,
  onChange,
  hint = '',
  error,
  placeholder,
  type = 'text',
  inputMode,
  optional = false,
  disabled = false,
  focusOnMount = false,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  type?: 'text' | 'number' | 'url' | 'email';
  inputMode?: 'numeric' | 'text' | 'url' | 'email';
  /** Помечаем редкое: обязательных полей в кабинете большинство. */
  optional?: boolean;
  disabled?: boolean;
  focusOnMount?: boolean;
  /**
   * Уход из поля. Нужен полям, которые приводят введённое к машинному виду:
   * делать это на каждой нажатой клавише нельзя — человек, печатающий
   * «seeno.ru», после точки терял бы её же.
   */
  onBlur?: () => void;
}) {
  const id = useId();
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);

  return (
    <Field
      id={id}
      label={label}
      hint={hint}
      error={error ?? undefined}
      optional={optional}
    >
      {(props) => (
        <input
          {...props}
          ref={ref}
          type={type}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
        />
      )}
    </Field>
  );
}

/* ──────────────────────────────── абзац ──────────────────────────────────── */

export function TextArea({
  label,
  value,
  onChange,
  hint = '',
  error,
  placeholder,
  rows = 3,
  focusOnMount = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | null;
  placeholder?: string;
  rows?: number;
  focusOnMount?: boolean;
}) {
  const id = useId();
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusOnMount) ref.current?.focus();
  }, [focusOnMount]);

  return (
    <Field id={id} label={label} hint={hint} error={error ?? undefined}>
      {(props) => (
        <textarea
          {...props}
          ref={ref}
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

/* ──────────────────────────────── выбор ──────────────────────────────────── */

/**
 * Родной `<select>` — по app-kit.md §2 свой список заводится только там, где
 * нужен множественный выбор. Тип значения параметризован: стадия воронки и вид
 * синонима — это перечисления контракта, и превращать их по дороге через поле
 * в безымянную строку значит потерять проверку на месте, где она и нужна.
 */
export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  hint = '',
  error,
  disabled = false,
}: {
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { value: T; label: string }[];
  hint?: string;
  error?: string | null;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <Field id={id} label={label} hint={hint} error={error ?? undefined}>
      {(props) => (
        <select
          {...props}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value as T)}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/* ─────────────────────────── набор значений ──────────────────────────────── */

/**
 * Значения поля вместе с недовведённым — то, что человек видит на экране.
 *
 * Этим форма собирает то, что отправляет. Набранное, но не подтверждённое
 * Enter-ом слово — с точки зрения человека такая же часть формы, как всё
 * остальное: он его вписал и нажал «Сохранить».
 */
export function chipsValue(values: string[], draft: string): string[] {
  const clean = draft.trim().replace(/,$/, '').trim();
  if (!clean) return values;
  if (values.some((value) => value.toLowerCase() === clean.toLowerCase())) return values;
  return [...values, clean];
}

/**
 * Города, в которых спрашивают про бренд, — список произвольной длины.
 *
 * Значение добавляется Enter-ом, запятой и при уходе с поля. Backspace на пустом
 * поле убирает последнее — это ожидаемое поведение любого поля с чипами, и без
 * него правка с клавиатуры невозможна.
 *
 * **Недовведённое слово живёт в состоянии ФОРМЫ, а не внутри этого поля,** и
 * это единственное, ради чего у компонента есть `draft` и `onDraftChange`.
 *
 * Приёмка: в карточке бренда вписана «Haval», нажато «Сохранить» — карточка
 * сохранилась, марка нет, текст остался в поле. Уход с поля здесь был, и в
 * большинстве случаев он успевал сработать; но обработчик отправки формы
 * замкнут на значения ТОГО ЖЕ прохода отрисовки, а `onChange` из `onBlur`
 * меняет состояние родителя — и увидит его только следующий проход. Успевает
 * или нет — решает порядок событий браузера, то есть удача.
 *
 * Полагаться на удачу там, где молча теряется введённое клиентом, нельзя. С
 * поднятым черновиком отправка читает его напрямую (`chipsValue`) и не зависит
 * ни от какого порядка событий: пока слово видно на экране, оно уедет на сервер.
 * Обязательность обоих свойств — не строгость ради строгости: новое чип-поле,
 * добавленное мимо этого правила, не соберётся.
 */
export function Chips({
  label,
  values,
  onChange,
  draft,
  onDraftChange,
  hint = '',
  placeholder,
  error,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  /** Набранное, но ещё не подтверждённое слово. Хранит форма. */
  draft: string;
  onDraftChange: (draft: string) => void;
  hint?: string;
  placeholder?: string;
  error?: string | null;
}) {
  const id = useId();

  const add = (raw: string) => {
    onDraftChange('');
    const next = chipsValue(values, raw);
    if (next !== values) onChange(next);
  };

  return (
    <Field id={id} label={label} hint={hint} error={error ?? undefined}>
      {(props) => (
        <>
          {values.length > 0 ? (
            <ul className="chip-row">
              {values.map((value) => (
                <li key={value} className="chip">
                  {value}
                  <button
                    type="button"
                    aria-label={`Убрать ${value}`}
                    onClick={() => onChange(values.filter((kept) => kept !== value))}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <input
            {...props}
            value={draft}
            placeholder={placeholder}
            onChange={(event) => {
              const text = event.target.value;
              if (text.endsWith(',')) add(text);
              else onDraftChange(text);
            }}
            onBlur={() => add(draft)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                add(draft);
              }
              if (event.key === 'Backspace' && !draft && values.length > 0) {
                onChange(values.slice(0, -1));
              }
            }}
          />
        </>
      )}
    </Field>
  );
}

/* ──────────────────────────────── флажок ─────────────────────────────────── */

export function Checkbox({
  label,
  checked,
  onChange,
  note,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Что случится, если оставить отмеченным. Не повтор подписи. */
  note?: string;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <div className="check">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        aria-describedby={note ? `${id}-note` : undefined}
        onChange={(event) => onChange(event.target.checked)}
      />
      <label htmlFor={id}>{label}</label>
      {note ? (
        <span id={`${id}-note`} className="check-note stamp">
          {note}
        </span>
      ) : null}
    </div>
  );
}

/* ────────────────────────────── переключатель ────────────────────────────── */

/**
 * Только для того, что применяется сразу (app-kit.md §2). В ядре им включают и
 * выключают вопрос: кнопки «сохранить» у строки списка нет и быть не должно.
 */
export function Switch({
  label,
  checked,
  onChange,
  disabled = false,
  busy = false,
  note,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Запрос ушёл. Контрол показывает прежнее значение, пока не пришёл ответ. */
  busy?: boolean;
  /**
   * Почему выключить нельзя. Выключенный контрол без объяснения — тупик
   * (app-kit.md §2), а «серый и молчит» в продукте с тарифными лимитами
   * встречается ровно там, где клиент упёрся в оплаченный потолок.
   */
  note?: string;
}) {
  const id = useId();
  return (
    <span className="switch-cell">
      <button
        id={id}
        type="button"
        className="switch"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        aria-describedby={note ? `${id}-note` : undefined}
        disabled={disabled || busy}
        onClick={() => onChange(!checked)}
      >
        <span className="switch-track" aria-hidden="true" />
      </button>
      {note ? (
        <span id={`${id}-note`} className="stamp">
          {note}
        </span>
      ) : null}
    </span>
  );
}

/* ──────────────────────────── подтверждение ──────────────────────────────── */

/**
 * Спрашивается только про необратимое, и кнопка **называет действие**:
 * «Удалить бренд», а не «ОК» (app-kit.md §4). Человек, читающий одну кнопку без
 * вопроса, должен понять, на что соглашается.
 */
export function Confirm({
  open,
  title,
  action,
  children,
  onConfirm,
  onCancel,
  danger = false,
}: {
  open: boolean;
  title: string;
  /** Текст кнопки: глагол и объект. */
  action: string;
  children: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <button
            type="button"
            className={danger ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
          >
            {action}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            Отмена
          </button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}
